import axios from 'axios';
import { authService } from './authService';
import { showToast } from '../components/Toast';
import { noteResponse, noteNetworkError } from './connectionStatus';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  config => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Tell connectionStatus whether the server answered, so the app can say when it
// cannot be reached instead of quietly showing saved data.
const reportResponse = response => {
  noteResponse(response.status, name => response.headers?.[name]);
  return response;
};

const reportError = error => {
  if (error.response) {
    noteResponse(error.response.status, name => error.response.headers?.[name]);
  } else if (error.code !== 'ERR_CANCELED') {
    noteNetworkError();
  }
  return Promise.reject(error);
};

// Sign-in, registration, password reset and the public machine page call the
// global axios rather than this instance, and sign-in is often the first request
// after an outage starts, so they report too. This module loads with App.
if (axios.interceptors) {
  axios.interceptors.response.use(reportResponse, reportError);
}

// Handle auth errors
axiosInstance.interceptors.response.use(
  reportResponse,
  error => {
    if (error.response) {
      // 401 Unauthorized - token invalid or expired, logout user
      if (error.response.status === 401) {
        // Logout will trigger event listeners and clean up localStorage
        authService.logout();
        // The App component will handle the redirect via the logout event
      }
      // 403 Forbidden - user authenticated but lacks permission
      else if (error.response.status === 403) {
        // Show user-friendly toast notification
        showToast("You don't have permission to perform this action", 'warning', 5000);
      }
    }
    return reportError(error);
  }
);

export const apiService = {
  // Users
  getUsers: () => axiosInstance.get('/users'),
  searchUsers: (query) => axiosInstance.get('/users', { params: { search: query } }),
  getUser: (id) => axiosInstance.get(`/users/${id}`),
  getUserByQR: (qrCode) => axiosInstance.get(`/users/qr/${qrCode}`),
  createUser: (data) => axiosInstance.post('/users', data),
  updateUser: (id, data) => axiosInstance.put(`/users/${id}`, data),
  deleteUser: (id) => axiosInstance.delete(`/users/${id}`),
  regenerateQRCode: (id) => axiosInstance.put(`/users/${id}/qr-code`),
  resetUserPassword: (id) => axiosInstance.post(`/users/${id}/reset-password`),
  exportMembers: () => axiosInstance.get('/users/export', { responseType: 'blob' }),
  getCurrentUser: () => axiosInstance.get('/users/me'),
  updateTabSettings: (id, data) => axiosInstance.put(`/users/${id}/tab-settings`, data),
  setPinGolfHandicapSeed: (id, data) => axiosInstance.put(`/users/${id}/pingolf-handicap-seed`, data),
  // Profile photo (multipart, field "photo"). Members set their own; admins anyone's.
  uploadProfilePhoto: (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    // Overrides the instance's JSON default, as uploadTaskAttachments does.
    return axiosInstance.put(`/users/${id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteProfilePhoto: (id) => axiosInstance.delete(`/users/${id}/photo`),
  uploadEventPhoto: (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    return axiosInstance.put(`/events/${id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteEventPhoto: (id) => axiosInstance.delete(`/events/${id}/photo`),

  // Credits
  getCreditBalance: (userId) => axiosInstance.get(`/credits/balance/${userId}`),
  getCreditHistory: (userId, params) => axiosInstance.get(`/credits/history/${userId}`, { params }),
  addCredits: (data) => axiosInstance.post('/credits/add', data),
  deductCredits: (data) => axiosInstance.post('/credits/deduct', data),
  purchaseGuestPass: (data) => axiosInstance.post('/credits/guest-pass', data),
  purchaseShopItem: (data) => axiosInstance.post('/credits/shop-purchase', data),
  donate: (data) => axiosInstance.post('/credits/donate', data),
  updateTransaction: (transactionId, data) => axiosInstance.put(`/credits/${transactionId}`, data),
  deleteTransaction: (transactionId) => axiosInstance.delete(`/credits/${transactionId}`),

  // Devices
  getDevices: () => axiosInstance.get('/devices'),
  getDeviceStatus: () => axiosInstance.get('/devices/status'),
  getDeviceDetails: (id) => axiosInstance.get(`/devices/${id}`),
  registerDevice: (data) => axiosInstance.post('/devices/register', data),
  updateDevice: (id, data) => axiosInstance.put(`/devices/${id}`, data),
  deleteDevice: (id, confirmation) => axiosInstance.delete(`/devices/${id}`, { data: { confirmation } }),
  testScan: (simulateSuccess = true) => axiosInstance.post('/devices/test-scan', { simulateSuccess }),
  reconcileDevice: (id, data) => axiosInstance.post(`/devices/${id}/reconcile`, data),
  getDeviceReconciliations: (id) => axiosInstance.get(`/devices/${id}/reconciliations`),
  getDeviceLogs: (id, params) => axiosInstance.get(`/devices/${id}/logs`, { params }),
  addRemoteCredit: (deviceId, credits = 1.0) => axiosInstance.post(`/devices/${deviceId}/remote-credit`, { credits }),

  // Machine Issues
  getMachineIssues: (params) => axiosInstance.get('/machines/issues/all', { params }),
  reportMachineIssue: (machineId, data) => axiosInstance.post(`/machines/${machineId}/report-issue`, data),
  getMachineIssueAssignableUsers: () => axiosInstance.get('/machines/issues/assignable-users'),
  triageIssue: (issueId, data) => axiosInstance.patch(`/machines/issues/${issueId}/triage`, data),
  claimIssue: (issueId) => axiosInstance.post(`/machines/issues/${issueId}/claim`),
  escalateIssue: (issueId, data) => axiosInstance.patch(`/machines/issues/${issueId}/escalate`, data),
  resolveIssue: (issueId, data) => axiosInstance.patch(`/machines/issues/${issueId}/resolve`, data),
  reopenIssue: (issueId, data) => axiosInstance.patch(`/machines/issues/${issueId}/reopen`, data),
  reassignIssue: (issueId, assignedTo) => axiosInstance.patch(`/machines/issues/${issueId}/reassign`, { assignedTo }),

  // Known Issues
  getKnownIssues: (machineId) => axiosInstance.get(`/machines/${machineId}/known-issues`),
  createKnownIssue: (data) => axiosInstance.post('/known-issues', data),
  updateKnownIssue: (id, data) => axiosInstance.patch(`/known-issues/${id}`, data),
  retireKnownIssue: (id) => axiosInstance.patch(`/known-issues/${id}/retire`),

  // Machine Maintenance
  getChecklistItems: (params) => axiosInstance.get('/maintenance/checklist-items', { params }),
  createChecklistItem: (data) => axiosInstance.post('/maintenance/checklist-items', data),
  updateChecklistItem: (id, data) => axiosInstance.put(`/maintenance/checklist-items/${id}`, data),
  deactivateChecklistItem: (id) => axiosInstance.post(`/maintenance/checklist-items/${id}/deactivate`),
  reactivateChecklistItem: (id) => axiosInstance.post(`/maintenance/checklist-items/${id}/reactivate`),
  getMaintenanceConfig: () => axiosInstance.get('/maintenance/config'),
  updateMaintenanceConfig: (intervalDays) => axiosInstance.put('/maintenance/config', { intervalDays }),
  getNeedsMaintenance: () => axiosInstance.get('/maintenance/needs-maintenance'),
  getMyMaintenanceRecords: () => axiosInstance.get('/maintenance/my-records'),
  getMaintenanceAssignableUsers: () => axiosInstance.get('/maintenance/assignable-users'),
  getCurrentMaintenanceRecord: (machineId) => axiosInstance.get(`/maintenance/machines/${machineId}/current-record`),
  getMaintenanceHistory: (machineId) => axiosInstance.get(`/maintenance/machines/${machineId}/history`),
  claimMaintenanceRecord: (machineId) => axiosInstance.post(`/maintenance/machines/${machineId}/claim`),
  unclaimMaintenanceRecord: (machineId) => axiosInstance.post(`/maintenance/machines/${machineId}/unclaim`),
  releaseMaintenanceRecord: (machineId) => axiosInstance.post(`/maintenance/machines/${machineId}/release`),
  assignMaintenanceRecord: (machineId, assignedTo) => axiosInstance.post(`/maintenance/machines/${machineId}/assign`, { assignedTo }),
  completeMaintenanceItem: (itemId) => axiosInstance.post(`/maintenance/items/${itemId}/complete`),
  uncompleteMaintenanceItem: (itemId) => axiosInstance.post(`/maintenance/items/${itemId}/uncomplete`),

  // Reports
  getOutstandingTabs: () => axiosInstance.get('/reports/outstanding-tabs'),
  
  // Device Revenue
  getDeviceRevenue: () => axiosInstance.get('/reports/device-revenue'),
  getDeviceRevenueDetails: (deviceId) => axiosInstance.get(`/reports/device-revenue/${deviceId}`),
  getDeviceRevenueDaily: (deviceId, params) => axiosInstance.get(`/reports/device-revenue/${deviceId}/daily`, { params }),
  getDeviceTodayPlays: (deviceId) => axiosInstance.get(`/reports/device-revenue/${deviceId}/today`),
  getDevicePlayerSpend: (deviceId) => axiosInstance.get(`/reports/device-revenue/${deviceId}/players`),

  // Give Backs (admin)
  getGiveBackPreview: (runWindow) => axiosInstance.get('/give-backs/preview', {
    params: runWindow ? { runWindow } : {}
  }),
  getGiveBackSettings: () => axiosInstance.get('/give-backs/settings'),
  updateGiveBackSettings: (data) => axiosInstance.put('/give-backs/settings', data),
  distributeGiveBacks: (data) => axiosInstance.post('/give-backs/distribute', data),
  resetCurrentPeriodGiveBackRun: (runWindow) => axiosInstance.delete('/give-backs/reset-current-period-test-run', {
    params: runWindow ? { runWindow } : {}
  }),

  // Membership Payments
  getUserPaymentHistory: (userId) => axiosInstance.get(`/membership-payments/user/${userId}`),
  getUnpaidPeriods: () => axiosInstance.get('/membership-payments/unpaid-periods'),
  recordPayment: (data) => axiosInstance.post('/membership-payments', data),
  updatePayment: (id, data) => axiosInstance.put(`/membership-payments/${id}`, data),
  deletePayment: (id) => axiosInstance.delete(`/membership-payments/${id}`),
  getMembershipSettings: (userId) => axiosInstance.get(`/membership-payments/settings/${userId}`),
  updateMembershipSettings: (userId, data) => axiosInstance.put(`/membership-payments/settings/${userId}`, data),

  // Onboarding
  submitApplication: (data) => axiosInstance.post('/onboarding/apply', data),
  getOnboardingStatus: () => axiosInstance.get('/onboarding/status'),
  getClubRules: () => axiosInstance.get('/onboarding/rules'),
  acknowledgeRules: (data) => axiosInstance.post('/onboarding/acknowledge-rules', data),
  
  // Onboarding Admin
  getApplications: () => axiosInstance.get('/onboarding/applications'),
  markOrientationComplete: (userId, data) => axiosInstance.post(`/onboarding/orientation/${userId}/complete`, data),
  markSubscriptionComplete: (userId, data) => axiosInstance.post(`/onboarding/subscription/${userId}/complete`, data),
  updateMembershipStatus: (userId, status) => axiosInstance.put(`/onboarding/membership-status/${userId}`, { status }),

  // Club Rules Admin
  getAllRules: () => axiosInstance.get('/rules'),
  getActiveRules: () => axiosInstance.get('/rules/active'),
  createRules: (data) => axiosInstance.post('/rules', data),
  updateRules: (id, data) => axiosInstance.put(`/rules/${id}`, data),
  deleteRules: (id) => axiosInstance.delete(`/rules/${id}`),
  getRuleAcknowledgments: (userId) => axiosInstance.get(`/rules/acknowledgments/${userId}`),
  // Documents a member signs: the club rules and the membership agreement.
  getDocumentStatus: () => axiosInstance.get('/rules/status'),
  getDocument: (key) => axiosInstance.get(`/rules/documents/${key}`),
  getMySignedDocuments: () => axiosInstance.get('/rules/my-documents'),
  getMySigningHistory: () => axiosInstance.get('/rules/my-history'),
  getSigningHistoryFor: (userId) => axiosInstance.get(`/rules/history/for/${userId}`),
  getSignedDocumentsFor: (userId) => axiosInstance.get(`/rules/documents/for/${userId}`),
  acknowledgeDocument: (data) => axiosInstance.post('/rules/acknowledge', data),
  getRulesCompliance: (doc) => axiosInstance.get('/rules/compliance', { params: { document: doc } }),
  getRulesOutstanding: (doc) => axiosInstance.get('/rules/outstanding', { params: { document: doc } }),
  getActiveDocument: (doc) => axiosInstance.get('/rules/active', { params: { document: doc } }),
  requireRulesAcknowledgment: (data) => axiosInstance.post('/rules/require-acknowledgment', data),
  markRulesReviewed: (data) => axiosInstance.post('/rules/mark-reviewed', data || {}),

  // Square Integration
  getSquareStatus: () => axiosInstance.get('/square/status'),
  getSquareSubscriptions: () => axiosInstance.get('/square/subscriptions'),
  getSquareSubscription: (subscriptionId) => axiosInstance.get(`/square/subscriptions/${subscriptionId}`),
  syncSquareInvoices: () => axiosInstance.post('/square/sync/invoices'),
  syncSquareAuto: () => axiosInstance.post('/square/sync/auto'),
  getSquareUserIntegration: (userId) => axiosInstance.get(`/square/user/${userId}`),
  getSquareUsers: () => axiosInstance.get('/square/users'),
  getSquareLocations: () => axiosInstance.get('/square/locations'),

  // Membership Dashboard
  getMembershipStatus: () => axiosInstance.get('/membership/status'),
  getMembershipPlans: () => axiosInstance.get('/membership/plans'),
  createMembershipPlan: (data) => axiosInstance.post('/membership/plans', data),
  updateMembershipPlan: (id, data) => axiosInstance.put(`/membership/plans/${id}`, data),
  deleteMembershipPlan: (id) => axiosInstance.delete(`/membership/plans/${id}`),
  
  // Membership Account Matching
  getAccountMatches: (status) => axiosInstance.get(`/membership/account-matches${status ? `?status=${status}` : ''}`),
  createAccountMatch: (data) => axiosInstance.post('/membership/account-matches', data),
  confirmAccountMatch: (id) => axiosInstance.post(`/membership/account-matches/${id}/confirm`),
  deleteAccountMatch: (id) => axiosInstance.delete(`/membership/account-matches/${id}`),
  autoMatchAccounts: () => axiosInstance.post('/membership/account-matches/auto-match'),
  
  // Membership Data Import
  importMembershipHistory: (records) => axiosInstance.post('/membership/import/history', { records }),
  getImportHistory: () => axiosInstance.get('/membership/import/history'),
  
  // Membership Analytics
  getMembershipStats: () => axiosInstance.get('/membership/stats'),
  getMembershipRevenue: (months = 12) => axiosInstance.get(`/membership/revenue?months=${months}`),
  getMembershipRetention: () => axiosInstance.get('/membership/retention'),
  bulkUpdateMembers: (userIds, action, value) => axiosInstance.post('/membership/bulk-update', { userIds, action, value }),
  getAllMembers: (params) => axiosInstance.get('/membership/members', { params }),
  
  // Past-Due Review

  // Account Merge
  getMergePreview: (sourceUserId, targetUserId) => axiosInstance.get(`/membership/merge-preview?sourceUserId=${sourceUserId}&targetUserId=${targetUserId}`),
  mergeAccounts: (sourceUserId, targetUserId, profileOverrides) => axiosInstance.post('/membership/merge', { sourceUserId, targetUserId, profileOverrides, confirm: true }),
  getMergeHistory: () => axiosInstance.get('/membership/merge-history'),

  // Monthly Member Counts
  getMonthlyMemberCounts: (months = 12) => axiosInstance.get(`/membership/monthly-counts?months=${months}`),
  refreshMonthlyMemberCounts: () => axiosInstance.post('/membership/monthly-counts/refresh'),

  // Account Claim (public endpoints)
  claimAccount: (data) => axios.post(`${API_URL}/auth/claim-account`, data),
  validateClaimToken: (token) => axios.get(`${API_URL}/auth/validate-claim-token/${token}`),

  // WordPress Content (public endpoint)
  getWordPressPage: (slug) => axios.get(`${API_URL}/automation/wordpress/pages/${slug}`),

  // Knowledge Base
  getKnowledgeBaseHome: (query) => axiosInstance.get('/knowledge-base', { params: query ? { q: query } : {} }),
  getKnowledgeBaseCategory: (slug) => axiosInstance.get(`/knowledge-base/categories/${slug}`),
  getKnowledgeBaseArticle: (slug) => axiosInstance.get(`/knowledge-base/articles/${slug}`),
  getKnowledgeBaseStatus: () => axiosInstance.get('/knowledge-base/status'),
  syncKnowledgeBase: () => axiosInstance.post('/knowledge-base/sync'),

  // Notifications
  getNotifications: (params) => axiosInstance.get('/notifications', { params }),
  getUnreadNotificationCount: () => axiosInstance.get('/notifications/unread-count'),
  sendNotification: (data) => axiosInstance.post('/notifications', data),
  markAllNotificationsRead: () => axiosInstance.patch('/notifications/read-all'),
  markNotificationRead: (id) => axiosInstance.patch(`/notifications/${id}/read`),
  deleteNotification: (id) => axiosInstance.delete(`/notifications/${id}`),
  clearNotifications: () => axiosInstance.delete('/notifications'),

  // Task Management - Feature roles (generic, reusable delegated-role grants)
  getUserFeatureRoles: (userId) => axiosInstance.get(`/users/${userId}/feature-roles`),
  grantFeatureRole: (userId, featureKey) => axiosInstance.post(`/users/${userId}/feature-roles`, { featureKey }),
  revokeFeatureRole: (userId, featureKey) => axiosInstance.delete(`/users/${userId}/feature-roles/${featureKey}`),

  // Task Management - Categories
  getTaskCategories: (params) => axiosInstance.get('/task-categories', { params }),
  createTaskCategory: (data) => axiosInstance.post('/task-categories', data),
  updateTaskCategory: (id, data) => axiosInstance.put(`/task-categories/${id}`, data),

  // Task Management - Templates
  getTaskTemplates: (params) => axiosInstance.get('/task-templates', { params }),
  getTaskTemplate: (id) => axiosInstance.get(`/task-templates/${id}`),
  createTaskTemplate: (data) => axiosInstance.post('/task-templates', data),
  updateTaskTemplate: (id, data) => axiosInstance.put(`/task-templates/${id}`, data),
  deactivateTaskTemplate: (id) => axiosInstance.post(`/task-templates/${id}/deactivate`),
  reactivateTaskTemplate: (id) => axiosInstance.post(`/task-templates/${id}/reactivate`),
  cloneTaskTemplate: (id, data = {}) => axiosInstance.post(`/task-templates/${id}/clone`, data),
  spawnTaskFromTemplate: (id) => axiosInstance.post(`/task-templates/${id}/spawn`),
  getWalkthroughChecklist: () => axiosInstance.get('/task-templates/walkthrough'),
  submitWalkthrough: (templateIds) => axiosInstance.post('/task-templates/walkthrough/submit', { templateIds }),

  // Task Management - Tasks
  getTasks: (params) => axiosInstance.get('/tasks', { params }),
  getTask: (id) => axiosInstance.get(`/tasks/${id}`),
  createTask: (data) => axiosInstance.post('/tasks', data),
  updateTask: (id, data) => axiosInstance.put(`/tasks/${id}`, data),
  cloneTask: (id, data = {}) => axiosInstance.post(`/tasks/${id}/clone`, data),
  assignTask: (id, userIds) => axiosInstance.post(`/tasks/${id}/assign`, { userIds }),
  unassignTask: (id, data) => axiosInstance.post(`/tasks/${id}/unassign`, data),
  claimTask: (id) => axiosInstance.post(`/tasks/${id}/claim`),
  releaseTask: (id) => axiosInstance.post(`/tasks/${id}/release`),
  completeTask: (id, data = {}) => axiosInstance.post(`/tasks/${id}/complete`, data),
  confirmTaskBounty: (id) => axiosInstance.post(`/tasks/${id}/confirm-bounty`),
  undoTaskCompletion: (id, data = {}) => axiosInstance.post(`/tasks/${id}/undo-completion`, data),
  reattributeTaskCompletion: (id, data) => axiosInstance.put(`/tasks/${id}/reattribute`, data),
  cancelTask: (id, data = {}) => axiosInstance.post(`/tasks/${id}/cancel`, data),
  reopenTask: (id) => axiosInstance.post(`/tasks/${id}/reopen`),
  dismissTask: (id) => axiosInstance.post(`/tasks/${id}/dismiss`),
  searchTasks: (q) => axiosInstance.get('/tasks/search', { params: { q } }),
  getTaskAssignableUsers: () => axiosInstance.get('/tasks/assignable-users'),
  getTasksNeedsAttention: () => axiosInstance.get('/tasks/needs-attention'),
  getTaskReporting: (params) => axiosInstance.get('/tasks/reporting', { params }),
  getTaskLeaderboard: (period) => axiosInstance.get('/tasks/leaderboard', { params: period ? { period } : {} }),
  getMyTaskStats: () => axiosInstance.get('/tasks/my-stats'),
  getTaskSpotlight: () => axiosInstance.get('/tasks/leaderboard/spotlight'),

  // Task Management - Suggestions
  getTaskSuggestions: (params) => axiosInstance.get('/task-suggestions', { params }),
  createTaskSuggestion: (data) => axiosInstance.post('/task-suggestions', data),
  approveTaskSuggestion: (id, data = {}) => axiosInstance.post(`/task-suggestions/${id}/approve`, data),
  rejectTaskSuggestion: (id) => axiosInstance.post(`/task-suggestions/${id}/reject`),

  // Task Management - Screenshots (owner: task | template | suggestion | completion)
  getTaskAttachments: (owner, ownerId) => axiosInstance.get('/task-attachments', { params: { owner, ownerId } }),
  uploadTaskAttachments: (owner, ownerId, files) => {
    const form = new FormData();
    form.append('owner', owner);
    form.append('ownerId', String(ownerId));
    files.forEach((file) => form.append('files', file));
    // Overrides the instance's JSON default, which would make axios serialise the
    // FormData to JSON. In the browser axios then drops it so the boundary is set.
    return axiosInstance.post('/task-attachments', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getTaskAttachmentFile: (id) => axiosInstance.get(`/task-attachments/${id}/file`, { responseType: 'blob' }),
  deleteTaskAttachment: (id) => axiosInstance.delete(`/task-attachments/${id}`),

  // Badges (generic, reusable beyond task management)
  getMyBadges: () => axiosInstance.get('/badges/mine'),
  getBadgeDefinitions: () => axiosInstance.get('/badges/definitions'),
  createBadgeDefinition: (data) => axiosInstance.post('/badges/definitions', data),
  updateBadgeDefinition: (id, data) => axiosInstance.put(`/badges/definitions/${id}`, data),
  getUserBadges: (userId) => axiosInstance.get(`/badges/users/${userId}`),
  awardBadge: (userId, badgeDefinitionId) => axiosInstance.post(`/badges/users/${userId}`, { badgeDefinitionId }),
  revokeBadge: (userId, badgeDefinitionId) => axiosInstance.delete(`/badges/users/${userId}/${badgeDefinitionId}`),

  // Transformers Pinball Championship
  createCompetition: (data) => axiosInstance.post('/competitions', data),
  getCurrentCompetition: () => axiosInstance.get('/competitions/current'),
  getCompetitions: () => axiosInstance.get('/competitions'),
  getCompetition: (id) => axiosInstance.get(`/competitions/${id}`),
  startCompetitionSeason: (id, data) => axiosInstance.post(`/competitions/${id}/start-season`, data),
  getCompetitionScheduledWeeks: (id) => axiosInstance.get(`/competitions/${id}/scheduled-weeks`),
  setCompetitionScheduledWeeks: (id, codes) => axiosInstance.put(`/competitions/${id}/scheduled-weeks`, { codes }),
  deleteCompetitionScheduledWeek: (id, roundNumber) => axiosInstance.delete(`/competitions/${id}/scheduled-weeks/${roundNumber}`),
  getCompetitionRounds: (id) => axiosInstance.get(`/competitions/${id}/rounds`),
  getCompetitionRoundStandings: (id, roundId) => axiosInstance.get(`/competitions/${id}/rounds/${roundId}`),
  getCompetitionRoundRecap: (id, roundId) => axiosInstance.get(`/competitions/${id}/rounds/${roundId}/recap`),
  getCompetitionTvDashboard: (id) => axiosInstance.get(`/competitions/${id}/tv-dashboard`),
  syncCompetitionRound: (id, roundId) => axiosInstance.post(`/competitions/${id}/rounds/${roundId}/sync`),
  closeCompetitionWeek: (id, roundId, data) => axiosInstance.post(`/competitions/${id}/rounds/${roundId}/close-and-open-next`, data),
  updateCompetitionRoundAverage: (id, roundId, machineAverageScore) => axiosInstance.patch(`/competitions/${id}/rounds/${roundId}/average`, { machineAverageScore }),
  getCompetitionLeaderboard: (id) => axiosInstance.get(`/competitions/${id}/leaderboard`),
  getUserCompetitionStatus: (userId) => axiosInstance.get(`/competitions/user/${userId}/status`),
  getUserCompetitionHistory: (userId) => axiosInstance.get(`/competitions/user/${userId}/history`),
  getUserCompetitionPlacements: (userId) => axiosInstance.get(`/competitions/user/${userId}/placements`),
  getCompetitionPlayers: (id) => axiosInstance.get(`/competitions/${id}/players`),
  updateCompetitionPlayer: (id, playerKey, data) => axiosInstance.patch(`/competitions/${id}/players/${encodeURIComponent(playerKey)}`, data),
  removeCompetitionPlayer: (id, playerKey) => axiosInstance.delete(`/competitions/${id}/players/${encodeURIComponent(playerKey)}`),
  declareCompetitionGrandChampion: (id, data) => axiosInstance.post(`/competitions/${id}/declare-grand-champion`, data),
  deleteCompetition: (id) => axiosInstance.delete(`/competitions/${id}`),
  deleteCompetitionRound: (roundId) => axiosInstance.delete(`/competitions/rounds/${roundId}`),

  clearPingolfHoleResult: (eventId, cardId, holeId) => axiosInstance.delete(`/events/${eventId}/pingolf-cards/${cardId}/hole-results/${holeId}`),

  // The club's Events TV screen: the next few weeks, public, no host emails.
  getTvEvents: (previewEventId) => axiosInstance.get('/events/tv', {
    params: previewEventId ? { event: previewEventId } : undefined
  }),

  // PinGolf League (season play)
  getPingolfSeasons: () => axiosInstance.get('/pingolf-seasons'),
  getMyPingolfSummary: () => axiosInstance.get('/pingolf-seasons/my-summary'),
  getMyPingolfHandicap: () => axiosInstance.get('/pingolf-seasons/my-handicap'),
  getPingolfSeason: (id) => axiosInstance.get(`/pingolf-seasons/${id}`),
  getPingolfSeasonStandings: (id) => axiosInstance.get(`/pingolf-seasons/${id}/standings`),
  // The rules prose is rendered on the server (config/pingolfRulesContent.js) so the app and
  // the club's WordPress page cannot drift apart. Fetched on its own rather than ridden along
  // on getPingolfSeason, which the season page re-polls every 15 seconds.
  getPingolfSeasonRules: (id) => axiosInstance.get(`/pingolf-seasons/${id}/rules`),
  // Self-scoped: returns the authenticated caller's own week-by-week results, no user id.
  getPingolfSeasonMyWeeks: (id) => axiosInstance.get(`/pingolf-seasons/${id}/my-weeks`),
  getPingolfSeasonWeeks: (id) => axiosInstance.get(`/pingolf-seasons/${id}/weeks`),
  getPingolfSeasonWeek: (weekId) => axiosInstance.get(`/pingolf-seasons/weeks/${weekId}`),
  getPingolfWeekNetBoard: (weekId) => axiosInstance.get(`/pingolf-seasons/weeks/${weekId}/net-board`),
  createPingolfSeason: (data) => axiosInstance.post('/pingolf-seasons', data),
  generatePingolfSeasonWeeks: (id) => axiosInstance.post(`/pingolf-seasons/${id}/generate-weeks`),
  // Re-applies every machine's current target to the season's unlocked upcoming holes and
  // week lineups. Returns { holesUpdated, weeksUpdated, overriddenSkipped, coursesBuilt }.
  refreshPingolfSeasonTargets: (id) => axiosInstance.post(`/pingolf-seasons/${id}/refresh-targets`),
  finalizePingolfSeason: (id) => axiosInstance.post(`/pingolf-seasons/${id}/finalize`),
  // confirmName must echo the season's exact name - the server rejects the delete without it,
  // since this also reverses handicaps, payouts and badges on live accounts.
  deletePingolfSeason: (id, confirmName) =>
    axiosInstance.delete(`/pingolf-seasons/${id}`, { data: { confirmName } }),
  setPingolfWeekLineup: (weekId, lineup) => axiosInstance.put(`/pingolf-seasons/weeks/${weekId}/lineup`, { lineup }),
  attachPingolfWeekSession: (weekId, data) => axiosInstance.post(`/pingolf-seasons/weeks/${weekId}/sessions`, data),
  createPingolfWeekSession: (weekId, data) => axiosInstance.post(`/pingolf-seasons/weeks/${weekId}/create-session`, data),
  removePingolfWeekSession: (weekId, sessionId) => axiosInstance.delete(`/pingolf-seasons/weeks/${weekId}/sessions/${sessionId}`),
  resyncPingolfSessionCourse: (weekId, sessionId) => axiosInstance.post(`/pingolf-seasons/weeks/${weekId}/sessions/${sessionId}/resync-course`),
  finalizePingolfWeek: (weekId) => axiosInstance.post(`/pingolf-seasons/weeks/${weekId}/finalize`),
  getPingolfWeekSettlement: (weekId) => axiosInstance.get(`/pingolf-seasons/weeks/${weekId}/settlement`),
  setPingolfSeasonStartDate: (id, startDate) => axiosInstance.put(`/pingolf-seasons/${id}/start-date`, { startDate }),
  getPingolfPayoutStructures: () => axiosInstance.get('/pingolf-seasons/payout-structures'),
  updatePingolfSeasonSettings: (id, settings) => axiosInstance.put(`/pingolf-seasons/${id}/settings`, settings),
  updatePingolfSeasonCardPrice: (id, cardPriceDollars) => axiosInstance.put(`/pingolf-seasons/${id}/card-price`, { cardPriceDollars }),
  getPingolfHostSlots: (id) => axiosInstance.get(`/pingolf-seasons/${id}/host-slots`),
  addPingolfHostSlot: (id, data) => axiosInstance.post(`/pingolf-seasons/${id}/host-slots`, data),
  removePingolfHostSlot: (id, slotId) => axiosInstance.delete(`/pingolf-seasons/${id}/host-slots/${slotId}`),
  generatePingolfHostNights: (id) => axiosInstance.post(`/pingolf-seasons/${id}/generate-host-nights`),

  // Events & RSVPs - Most methods use axiosInstance for auth, public endpoints use axios directly
  getMyHostedEvents: () => axiosInstance.get('/events/my-hosted-events'),
  getMyRsvps: () => axiosInstance.get('/users/me/rsvps'),

  // Derived membership status (docs/MEMBERSHIP_SQUARE_REQUIREMENTS.md).
  // Status is computed from payment evidence, not read from a flag, so these
  // are the only endpoints a screen should use to ask "is this person a member".
  getMemberStatus: (userId) => axiosInstance.get(`/membership-status/users/${userId}`),
  refreshMemberStatus: (userId) => axiosInstance.post(`/membership-status/users/${userId}/refresh`),
  setPaymentMethod: (userId, paymentMethod, cashPaidThrough) =>
    axiosInstance.put(`/membership-status/users/${userId}/payment-method`, { paymentMethod, cashPaidThrough }),
  getMembershipQueue: () => axiosInstance.get('/membership-status/queue'),
  // Records a judgement ("these are different people") so the same row is not
  // raised again. Not a snooze: nothing in the queue is hidden on a timer.
  dismissQueueItem: (itemKey) =>
    axiosInstance.post(`/membership-status/queue/${encodeURIComponent(itemKey)}/snooze`, {}),
  // A permanent judgement that two accounts are two different people, so the
  // pair stops being offered as a possible duplicate.
  markNotDuplicates: (userIds) =>
    axiosInstance.post('/membership-status/queue/not-duplicates', { userIds }),
  restoreQueueItem: (itemKey) =>
    axiosInstance.delete(`/membership-status/queue/${encodeURIComponent(itemKey)}/snooze`),
  // Defaults to a dry run. Applying requires apply: true, deliberately.
  runMembershipReconcile: (apply = false, userIds) =>
    axiosInstance.post('/membership-status/reconcile', { apply, userIds }),
  getUnassignedSubscriptions: () => axiosInstance.get('/membership-status/sponsorships/unassigned'),
  // `period` carries startsOn and optionally endsOn. An arrangement that has
  // already ended is recorded as history rather than as something running now.
  linkSponsorship: (beneficiaryUserId, sponsorUserId, period = {}) =>
    axiosInstance.post('/membership-status/sponsorships', {
      beneficiaryUserId, sponsorUserId, startsOn: period.startsOn || null, endsOn: period.endsOn || null
    }),
  unlinkSponsorship: (beneficiaryUserId) =>
    axiosInstance.delete(`/membership-status/sponsorships/${beneficiaryUserId}`),
  endMembership: (userId, data) => axiosInstance.post(`/membership-status/users/${userId}/end`, data),
  cancelEndMembership: (userId) => axiosInstance.delete(`/membership-status/users/${userId}/end`),
  getOffboardingChecklists: () => axiosInstance.get('/membership-status/offboarding'),
  completeOffboardingStep: (id, stepKey) =>
    axiosInstance.post(`/membership-status/offboarding/${id}/steps/${encodeURIComponent(stepKey)}`),
  getMemberEmails: (userId) => axiosInstance.get(`/membership-status/users/${userId}/emails`),
  addMemberEmail: (userId, email) => axiosInstance.post(`/membership-status/users/${userId}/emails`, { email }),
  removeMemberEmail: (userId, email) =>
    axiosInstance.delete(`/membership-status/users/${userId}/emails/${encodeURIComponent(email)}`),
  linkSquareCustomer: (squareCustomerId, userId) =>
    axiosInstance.post(`/membership-status/square-customers/${encodeURIComponent(squareCustomerId)}/link`, { userId }),
  detachSquareCustomer: (squareCustomerId) =>
    axiosInstance.delete(`/membership-status/square-customers/${encodeURIComponent(squareCustomerId)}/link`),
  categorisePayment: (transactionId, action, userId) =>
    axiosInstance.post(`/membership-status/payments/${transactionId}/categorise`, { action, userId }),
  grantOnboardingOverride: (userId) => axiosInstance.post(`/membership-status/users/${userId}/onboarding-override`),
  clearOnboardingOverride: (userId) => axiosInstance.delete(`/membership-status/users/${userId}/onboarding-override`),
  grantComp: (userId) => axiosInstance.post(`/membership-status/users/${userId}/comp`),
  revokeComp: (userId) => axiosInstance.delete(`/membership-status/users/${userId}/comp`),
  recordCashPayment: (userId, data) => axiosInstance.post(`/membership-status/users/${userId}/cash-payment`, data),

  get: (url, config) => axiosInstance.get(url, config),
  post: (url, data, config) => axiosInstance.post(url, data, config),
  put: (url, data, config) => axiosInstance.put(url, data, config),
  patch: (url, data, config) => axiosInstance.patch(url, data, config),
  delete: (url, config) => axiosInstance.delete(url, config)
};

export default apiService;
